import {
    Component,
    Input,
    OnDestroy,
    ChangeDetectionStrategy,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { AgGridAngular } from 'ag-grid-angular';
import {
    ColDef,
    GridApi,
    GridOptions,
    GridReadyEvent,
} from 'ag-grid-community';
import { Subject } from 'rxjs';
import { nameOf } from '../../../../../core/functions/string-helpers';
import { GridHelper } from '../../../../../core/models/grid/grid-helper';
import { RadioButtonCellRendererComponent } from '../../../../../shared/components/grid/cell-renderers/radio-button-cell-renderer/radio-button-cell-renderer.component';
import { GridApplicationTemplateRowData } from '../../../template/components/application-template-dashboard/models/grid-application-template-row-data';
import { GridApplicationTemplateRowResponse } from '../../../template/components/application-template-dashboard/models/grid-application-template-row-response';
import { ApplicationTemplateGridService } from '../../../template/shared/services/application-template-grid/application-template-grid.service';
import { ApplicationTemplateSelectionGridContext } from './models/application-template-selection-grid-context';

@Component({
    selector: 'app-application-template-selection',
    templateUrl: './application-template-selection.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [AgGridAngular],
})
export class ApplicationTemplateSelectionComponent implements OnDestroy {
    private destroy$ = new Subject<void>();

    @Input() templateApplicationMasterFormControl:
        | FormControl<number | null>
        | undefined;
    @Input()
    applicationTemplatesAvailableForSelection: GridApplicationTemplateRowResponse[] =
        [];

    constructor(
        private readonly applicationTemplateGridService: ApplicationTemplateGridService,
    ) {}

    context: unknown;
    rowData: GridApplicationTemplateRowData[] = [];
    gridApi: GridApi | undefined;

    columnDefs: ColDef[] = [
        {
            headerName: 'Select',
            field: nameOf<GridApplicationTemplateRowResponse>(
                'templateApplicationMasterId',
            ),
            filter: false,
            sortable: false,
            minWidth: 100,
            maxWidth: 100,
            pinned: 'left',
            cellRenderer: RadioButtonCellRendererComponent,
            ...GridHelper.disableCellFocusBorderColDef,
        },
        {
            field: nameOf<GridApplicationTemplateRowResponse>('name'),
            filter: false,
            maxWidth: 250,
        },
        {
            field: nameOf<GridApplicationTemplateRowResponse>('description'),
            filter: false,
            minWidth: 300,
        },
        {
            headerName: 'Created',
            field: nameOf<GridApplicationTemplateRowResponse>('createdAt'),
            ...GridHelper.defaultDateColDef,
            filter: false,
            suppressSizeToFit: true,
        },
        {
            headerName: 'Modified',
            field: nameOf<GridApplicationTemplateRowResponse>('updatedAt'),
            ...GridHelper.defaultDateColDef,
            filter: false,
            sort: 'desc',
            suppressSizeToFit: true,
        },
    ];

    gridOptions: GridOptions = {
        ...GridHelper.defaultGridOptions,
        ...GridHelper.getFormCellRendererOnCellFocusedGridOptions(),
    };

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    onGridReady(params: GridReadyEvent<unknown>) {
        this.gridApi = params.api;
        this.context = this.initializeGridContext();

        this.setupGridData();
    }

    private initializeGridContext(): ApplicationTemplateSelectionGridContext {
        const gridContext: ApplicationTemplateSelectionGridContext = {
            formControl: this.templateApplicationMasterFormControl!,
        };

        return gridContext;
    }

    private setupGridData() {
        this.rowData = this.applicationTemplateGridService.transformRowData(
            this.applicationTemplatesAvailableForSelection,
        );

        this.gridApi!.setGridOption('rowData', this.rowData);
        this.gridApi!.setGridOption('columnDefs', this.columnDefs);
    }
}
